import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, HERITAGE_ASSETS } from '@/lib/api';
import { Panel, SectionDivider } from '@/components/Panel';
import { HeritageSeal } from '@/components/BrandMark';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import { ArrowLeft, Ruler, ShoppingBag, Check, Film } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';

function ImgSrc(url) {
  if (!url) return HERITAGE_ASSETS.editorial;
  return url;
}

/** Detect video kind & build embed URL. Supports MP4 direct + YouTube + Vimeo. */
function parseVideo(url) {
  if (!url) return null;
  const u = url.trim();
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) return { kind: 'youtube', embed: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: 'vimeo', embed: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(u)) return { kind: 'mp4', embed: u };
  return { kind: 'mp4', embed: u }; // best-effort default
}

const BulletList = ({ items, testid }) => {
  if (!items || items.length === 0) {
    return <div className="text-sm italic opacity-70" style={{ color: 'var(--heritage-plum)' }}>Details coming soon.</div>;
  }
  return (
    <ul className="space-y-2 text-sm" style={{ color: 'var(--heritage-plum)' }} data-testid={testid}>
      {items.map((h) => (
        <li key={h} className="flex gap-2"><span>&#9670;</span><span>{h}</span></li>
      ))}
    </ul>
  );
};

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [selectedColour, setSelectedColour] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setHeroIdx(0);
    setSelectedColour(''); setSelectedSize('');
    (async () => {
      try {
        // Try shop endpoint first (variant-aware), fall back to legacy catalog
        let data;
        try {
          const shop = await api.get(`/shop/products/${slug}`);
          data = { item: shop.data.product };
        } catch (err) {
          if (err?.response?.status === 404) {
            const cat = await api.get(`/catalog-items/${slug}`);
            data = cat.data;
          } else {
            throw err;
          }
        }
        setProduct(data.item || null);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
        else console.error(e);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  // Derived: available colours, sizes, current variant
  const variants = product?.variants || [];
  const hasVariants = variants.length > 0;
  const availableColours = useMemo(
    () => Array.from(new Set(variants.map((v) => v.colour).filter(Boolean))),
    [variants],
  );
  const availableSizes = useMemo(
    () => Array.from(new Set(variants.map((v) => v.size).filter(Boolean))),
    [variants],
  );

  // Auto-select first colour/size when product loads
  useEffect(() => {
    if (availableColours.length === 1 && !selectedColour) setSelectedColour(availableColours[0]);
    if (availableSizes.length === 0 && variants.length === 1) {
      // Single-variant (socks) — auto-select the colour combo
      setSelectedColour(variants[0].colour || '');
    }
  }, [availableColours, availableSizes, variants, selectedColour]);

  const activeVariant = useMemo(() => {
    if (!hasVariants) return null;
    if (availableSizes.length > 0) {
      return variants.find((v) => v.colour === selectedColour && v.size === selectedSize) || null;
    }
    return variants.find((v) => v.colour === selectedColour) || variants[0] || null;
  }, [variants, hasVariants, selectedColour, selectedSize, availableSizes]);

  const inStock = activeVariant ? activeVariant.stock > 0 : (product?.in_stock ?? true);
  const stockCount = activeVariant?.stock;

  // Reset justAdded flag on variant change
  useEffect(() => { setJustAdded(false); }, [selectedColour, selectedSize]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="opacity-70" style={{ color: 'var(--heritage-plum)' }}>Loading product...</div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <HeritageSeal size={64} />
        <div className="h-eyebrow mt-4">Not found</div>
        <div className="h-display text-3xl mt-2" style={{ color: 'var(--heritage-plum)' }}>This piece is no longer available</div>
        <p className="opacity-80 mt-3 text-sm" style={{ color: 'var(--heritage-plum)' }}>
          Return to the store to see what is currently in the release.
        </p>
        <div className="mt-6">
          <Link to="/store" className="h-btn"><ArrowLeft size={14} className="mr-1.5" /> Back to store</Link>
        </div>
      </div>
    );
  }

  const hero = ImgSrc(product.image_base64 || product.image_url);
  const gallery = Array.from(new Set(
    [hero, ...(product.images || []), ...(product.detail_images || [])].filter(Boolean)
  ));
  const activeImg = gallery[heroIdx] || hero;

  const canAdd = hasVariants ? Boolean(activeVariant) && inStock : true;

  const handleAdd = () => {
    if (!canAdd || !activeVariant) return;
    addItem({
      slug: product.slug,
      sku: activeVariant.sku,
      name: product.name,
      colour: activeVariant.colour,
      size: activeVariant.size,
      unit_price: Number(product.sale_price || product.price || 0),
      image_url: hero,
      stock: activeVariant.stock,
      qty: 1,
    });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  return (
    <div style={{ background: 'var(--heritage-cream)' }}>
      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <Link to="/store" className="h-eyebrow inline-flex items-center gap-1.5 opacity-80 hover:opacity-100" data-testid="pdp-back">
          <ArrowLeft size={12} /> Back to the store
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-6 pb-12 grid lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div className="space-y-3">
          <Panel className="p-3 overflow-hidden">
            <img
              src={activeImg}
              alt={product.name}
              className="w-full aspect-[4/5] object-cover rounded-lg"
              data-testid="pdp-hero-image"
            />
          </Panel>
          {gallery.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {gallery.slice(0, 10).map((g, i) => (
                <button
                  key={`${g}-${i}`}
                  type="button"
                  onClick={() => setHeroIdx(i)}
                  className="aspect-square rounded-lg overflow-hidden border transition"
                  style={{
                    borderColor: i === heroIdx ? 'var(--heritage-plum)' : 'rgba(75,24,56,0.2)',
                    background: '#FBF8F1',
                    opacity: i === heroIdx ? 1 : 0.75,
                  }}
                  data-testid={`pdp-thumb-${i}`}
                >
                  <img src={g} alt={`view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="flex flex-col gap-4">
          <div>
            <div className="h-eyebrow">{product.collection_name || product.subtitle || 'Heritage Signature Collection'}</div>
            <h1 className="h-display text-4xl md:text-5xl leading-[1.05] mt-2" style={{ color: 'var(--heritage-plum)' }} data-testid="pdp-name">
              {product.name}
            </h1>
          </div>

          {product.short_description && (
            <p className="text-base opacity-90 leading-relaxed" style={{ color: 'var(--heritage-plum)' }} data-testid="pdp-short-desc">
              {product.short_description}
            </p>
          )}

          <div className="h-divider" />

          <div className="flex items-baseline gap-3">
            <div className="h-serif text-3xl" style={{ color: 'var(--heritage-plum)' }} data-testid="pdp-price">
              ${product.sale_price || product.price} {product.currency || 'NZD'}
            </div>
            {product.sale_price && (
              <div className="text-sm line-through opacity-60" style={{ color: 'var(--heritage-plum)' }}>${product.price}</div>
            )}
          </div>

          {/* Variant selectors */}
          {hasVariants && (
            <div className="space-y-4 mt-1">
              {availableColours.length > 0 && (
                <div>
                  <div className="h-eyebrow mb-2">Colour {selectedColour && <span className="opacity-70 normal-case">— {selectedColour}</span>}</div>
                  <div className="flex flex-wrap gap-2" data-testid="pdp-colour-options">
                    {availableColours.map((c) => {
                      const swatch = variants.find((v) => v.colour === c);
                      const isSelected = selectedColour === c;
                      const colourHex = swatch?.colour_hex || '#EAE0D2';
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setSelectedColour(c)}
                          className="inline-flex items-center gap-2 rounded-full py-1.5 px-3 border text-xs transition"
                          style={{
                            borderColor: isSelected ? 'var(--heritage-plum)' : 'rgba(75,24,56,0.25)',
                            background: isSelected ? 'rgba(75,24,56,0.06)' : 'transparent',
                            color: 'var(--heritage-plum)',
                          }}
                          data-testid={`pdp-colour-${c.replace(/\W+/g, '-').toLowerCase()}`}
                          aria-pressed={isSelected}
                        >
                          <span className="w-4 h-4 rounded-full border" style={{ background: colourHex, borderColor: 'rgba(75,24,56,0.4)' }} />
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {availableSizes.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="h-eyebrow">Size {selectedSize && <span className="opacity-70 normal-case">— {selectedSize}</span>}</div>
                    <Link to="/size-guide" className="text-[11px] opacity-75 hover:opacity-100 inline-flex items-center gap-1" style={{ color: 'var(--heritage-plum)' }}>
                      <Ruler size={11} /> Size guide
                    </Link>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="pdp-size-options">
                    {availableSizes.map((s) => {
                      const variant = variants.find((v) => v.size === s && v.colour === selectedColour);
                      const disabled = !selectedColour || !variant || variant.stock <= 0;
                      const isSelected = selectedSize === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setSelectedSize(s)}
                          disabled={disabled}
                          className="min-w-[52px] rounded-md py-2 px-4 border text-sm transition"
                          style={{
                            borderColor: isSelected ? 'var(--heritage-plum)' : 'rgba(75,24,56,0.25)',
                            background: isSelected ? 'var(--heritage-plum)' : 'transparent',
                            color: isSelected ? '#F6F0E4' : 'var(--heritage-plum)',
                            opacity: disabled ? 0.35 : 1,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            textDecoration: disabled ? 'line-through' : 'none',
                          }}
                          data-testid={`pdp-size-${s}`}
                          aria-pressed={isSelected}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stock feedback */}
              <div className="text-xs" style={{ color: 'var(--heritage-plum)' }} data-testid="pdp-stock">
                {!selectedColour && availableColours.length > 1 && <span className="opacity-70">Select a colour to see stock.</span>}
                {selectedColour && availableSizes.length > 0 && !selectedSize && <span className="opacity-70">Select a size to see stock.</span>}
                {activeVariant && activeVariant.stock > 0 && activeVariant.stock <= 10 && (
                  <span style={{ color: '#8A4E3A' }}>Only {activeVariant.stock} left in this size &amp; colour.</span>
                )}
                {activeVariant && activeVariant.stock > 10 && <span className="opacity-70">{activeVariant.stock} available.</span>}
                {activeVariant && activeVariant.stock === 0 && <span style={{ color: '#8A4E3A' }}>Sold out in this size &amp; colour.</span>}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              type="button"
              className="h-btn inline-flex items-center gap-2"
              onClick={handleAdd}
              disabled={!canAdd}
              style={{ opacity: canAdd ? 1 : 0.5, cursor: canAdd ? 'pointer' : 'not-allowed' }}
              data-testid="pdp-add-to-cart"
            >
              {justAdded ? <><Check size={14} /> Added to bag</> : <><ShoppingBag size={14} /> {!inStock ? 'Sold out' : 'Add to bag'}</>}
            </button>
            <Link to="/size-guide" className="h-btn-outline">
              <Ruler size={14} className="mr-1.5" /> Size guide
            </Link>
          </div>
        </div>
      </div>

      <SectionDivider label="The full story" />

      {(() => {
        const video = parseVideo(product.video_url);
        if (!video) return null;
        return (
          <div className="max-w-4xl mx-auto px-6 pb-4">
            <div className="mb-3 h-eyebrow flex items-center gap-1.5" style={{ color: 'var(--heritage-plum)' }}>
              <Film size={12} /> Video
            </div>
            <Panel className="p-2 overflow-hidden">
              <div className="relative w-full rounded-lg overflow-hidden" style={{ aspectRatio: '16 / 9', background: '#1a0f14' }}>
                {video.kind === 'mp4' ? (
                  <video src={video.embed} controls playsInline preload="metadata" className="w-full h-full object-cover" data-testid="pdp-video-mp4" />
                ) : (
                  <iframe
                    src={video.embed}
                    title={`${product.name} video`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    data-testid={`pdp-video-${video.kind}`}
                  />
                )}
              </div>
            </Panel>
          </div>
        );
      })()}

      <div className="max-w-4xl mx-auto px-6 pb-16">
        <Panel className="p-6 md:p-8">
          <Accordion type="multiple" defaultValue={['description']} className="w-full">
            {product.description && (
              <AccordionItem value="description" className="border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <AccordionTrigger className="h-serif text-xl" data-testid="pdp-acc-description">
                  <span style={{ color: 'var(--heritage-plum)' }}>Description</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm md:text-base leading-relaxed opacity-90 whitespace-pre-wrap" style={{ color: 'var(--heritage-plum)' }}>
                    {product.description}
                  </p>
                </AccordionContent>
              </AccordionItem>
            )}
            {(product.features || []).length > 0 && (
              <AccordionItem value="features" className="border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <AccordionTrigger className="h-serif text-xl" data-testid="pdp-acc-features">
                  <span style={{ color: 'var(--heritage-plum)' }}>Features</span>
                </AccordionTrigger>
                <AccordionContent>
                  <BulletList items={product.features} testid="pdp-features" />
                </AccordionContent>
              </AccordionItem>
            )}
            {(product.materials || []).length > 0 && (
              <AccordionItem value="materials" className="border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <AccordionTrigger className="h-serif text-xl" data-testid="pdp-acc-materials">
                  <span style={{ color: 'var(--heritage-plum)' }}>Materials</span>
                </AccordionTrigger>
                <AccordionContent>
                  <BulletList items={product.materials} testid="pdp-materials" />
                </AccordionContent>
              </AccordionItem>
            )}
            {(product.fit_notes || product.size_notes) && (
              <AccordionItem value="fit" className="border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <AccordionTrigger className="h-serif text-xl" data-testid="pdp-acc-fit">
                  <span style={{ color: 'var(--heritage-plum)' }}>Size &amp; fit</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm" style={{ color: 'var(--heritage-plum)' }}>
                    {product.fit_notes && (
                      <div>
                        <div className="h-eyebrow mb-1">Fit</div>
                        <p className="opacity-90 leading-relaxed">{product.fit_notes}</p>
                      </div>
                    )}
                    {product.size_notes && (
                      <div>
                        <div className="h-eyebrow mb-1">Size guide notes</div>
                        <p className="opacity-90 leading-relaxed">{product.size_notes}</p>
                      </div>
                    )}
                    <Link to="/size-guide" className="h-btn-outline text-xs w-fit mt-2">
                      <Ruler size={12} className="mr-1.5" /> Open size guide
                    </Link>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {(product.care_instructions || []).length > 0 && (
              <AccordionItem value="care" className="border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <AccordionTrigger className="h-serif text-xl" data-testid="pdp-acc-care">
                  <span style={{ color: 'var(--heritage-plum)' }}>Care instructions</span>
                </AccordionTrigger>
                <AccordionContent>
                  <BulletList items={product.care_instructions} testid="pdp-care" />
                </AccordionContent>
              </AccordionItem>
            )}
            {(product.heritage_details || []).length > 0 && (
              <AccordionItem value="heritage" className="border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <AccordionTrigger className="h-serif text-xl" data-testid="pdp-acc-heritage">
                  <span style={{ color: 'var(--heritage-plum)' }}>Heritage details</span>
                </AccordionTrigger>
                <AccordionContent>
                  <BulletList items={product.heritage_details} testid="pdp-heritage" />
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </Panel>
      </div>
    </div>
  );
}
